/* eslint-disable prettier/prettier */
import React, { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import AppBar from 'components/AppBar';
import ApplicationOverlayContent from 'components/ApplicationLayout/ApplicationOverlayContent';
import AutoDetailRates from 'components/AutoDetailRates';
import Loader from 'components/Loader';
import ModalHeader from 'components/ModalHeader';
import { Desktop, Mobile } from 'components/Responsive';
import TripCard from 'components/TripCard';
import useIsMobile from 'hooks/useIsMobile';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { mapPriceToVehicleRates } from 'models/auto/price';
import { AirFareConditions } from 'models/flightValidation';
import { RepprovalReason } from 'models/repprovalReason';
import { TravelDetails as TravelDetailsModel } from 'models/travelDetails';
import { expirationHours } from 'utils/generic';

import AutoDetailsSection from './components/AutoDetailsSection';
import Buttons from './components/Buttons';
import FlightDetailsSection from './components/FlightDetailsSection';
import Flights from './components/Flights';
import HotelDetailsSection from './components/HotelDetailsSection';
import Prices from './components/Prices';
import TravelTimeline from './components/Timeline';
import { SectionContainer, AddNewExpense } from './styles';

export interface Props {
  show: boolean;
  title: string;
  loading?: boolean;
  data: TravelDetailsModel;
  requestId?: string;
  userId: string;
  submitting?: boolean;
  reasons?: RepprovalReason[];
  onClose: () => void;
  onConfirm?: () => void;
  onDecline?: (reason?: RepprovalReason) => void;
  onChange?: (airFareConditions?: AirFareConditions[]) => void;
  onCancel?: () => void;
}

const TravelDetails = ({
  show,
  loading,
  title,
  data,
  requestId,
  userId,
  onClose,
  onConfirm,
  onDecline,
  onChange,
  onCancel,
  submitting,
  reasons
}: Props) => {
  const { t } = useStoreTranslation();
  const [showDetails, setShowDetails] = useState(false);
  const history = useHistory();
  const isMobile = useIsMobile();

  const adminView = !!onConfirm && !!onDecline;
  const showAddExpenseButton = data.requester?.code === userId;

  const handleShowDetails = () => {
    setShowDetails(!showDetails);
  };

  const total = useMemo(
    () => (data.prices && data.prices?.taxesPayableAtDestination ? data.prices?.vehicleTotal : undefined),
    [data.prices],
  );
  const expirationDate = useMemo(
    () =>
      data.air
        ? data.air?.expirationDate
        : data.hotel
          ? data.hotel.expirationDate
          : data.vehicle
            ? new Date(data.vehicle.expirationDate)
            : new Date(),
    [data],
  );

  const getExpiration = useCallback((date: Date) => expirationHours(date), []);

  if (data.type === 'Flight') {
    return <FlightDetailsSection
      show={show}
      onClose={onClose}
      onBack={onClose}
      onChange={onChange}
      data={data}
      onCancel={onCancel}
      travelId={requestId}
    />
  }

  return (
    <ApplicationOverlayContent width={800} show={show} onClose={onClose}>
      <Mobile>
        <AppBar goBack={onClose}>{title}</AppBar>
      </Mobile>

      {loading ? (
        <Loader fullScreen={isMobile} />
      ) : (
        <>
          <Desktop>
            <ModalHeader title={title} onClose={onClose} />
          </Desktop>

          <SectionContainer>
            <TripCard
              myTripsView
              adminView={adminView}
              travel={data}
              detailsOpen={showDetails}
              onToggle={handleShowDetails}
            >
              {adminView && data.requester.status !== 'Expired' && (
                <Buttons
                  requesterName={data.requester.fullName}
                  onConfirm={
                    data.requester.status !== 'Approved' && data.requester.status !== 'Reproved' ? onConfirm : undefined
                  }
                  onDecline={
                    data.requester.status !== 'Approved' && data.requester.status !== 'Reproved' ? onDecline : undefined
                  }
                  submitting={submitting}
                  reasons={reasons}
                  paymentFailure={data.paymentFailure}
                />
              )}
            </TripCard>
          </SectionContainer>

          {data.flightSectors && <Flights data={data} />}
          {data.hotel && data.hotel.name && (
            <HotelDetailsSection price={data.prices} hotel={data.hotel} requester={data.requester} />
          )}
          {data.vehicle && (
            <AutoDetailsSection
              auto={data.vehicle}
              requester={data.requester}
              confirmationCode={data.vehicle.orderNumber}
            />
          )}
          <SectionContainer>
            {data.travelLogs && data.travelLogs.length > 0 && <TravelTimeline data={data} />}
            {data.vehicle ? (
              <AutoDetailRates
                rates={mapPriceToVehicleRates(data.prices)}
                daysQty={data.vehicle.quantityDays}
                total={total}
                isHistory={true}
                status={data.requester.status}
                time={getExpiration(expirationDate)}
              />
            ) : (
              data.prices && <Prices data={data} />
            )}
            {showAddExpenseButton && (
              <AddNewExpense onClick={() => history.push('/expense', { id: requestId })} invertedColor>
                {t('travelDetails.expenses.addNew')}
              </AddNewExpense>
            )}
          </SectionContainer>
        </>
      )}
    </ApplicationOverlayContent>
  );
};

export default TravelDetails;
